/**
 * RoleTemplateSelector - 角色模板选择器组件
 * 用于从可用模板中选择并创建角色，支持预览和批量应用
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Card,
  Row,
  Col,
  Tag,
  Button,
  Space,
  Spin,
  Empty,
  Tooltip,
  Badge,
  Typography,
  Descriptions,
  List,
  Checkbox,
  message,
} from 'antd';
import {
  CrownOutlined,
  SettingOutlined,
  TeamOutlined,
  UserOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import type { RoleTemplate } from '@/modules/foundation/api/types';

const { Text, Title } = Typography;

/** 模板图标映射 */
const TEMPLATE_ICONS: Record<string, React.ReactNode> = {
  crown: <CrownOutlined />,
  setting: <SettingOutlined />,
  team: <TeamOutlined />,
  user: <UserOutlined />,
  eye: <EyeOutlined />,
};

/** 模板范围标签 */
const SCOPE_LABELS: Record<string, { text: string; color: string }> = {
  system: { text: '系统', color: 'red' },
  global: { text: '全局', color: 'blue' },
  tenant: { text: '租户', color: 'green' },
};

/** 数据范围标签 */
const DATA_SCOPE_LABELS: Record<string, string> = {
  all: '全部数据',
  custom: '自定义',
  dept: '本部门',
  dept_and_child: '本部门及下级',
  self: '仅本人',
};

export interface RoleTemplateSelectorProps {
  /** 是否显示 */
  open: boolean;
  /** 可用模板列表 */
  templates: RoleTemplate[];
  /** 加载状态 */
  loading?: boolean;
  /** 已选择的模板ID列表 */
  selectedIds?: number[];
  /** 是否允许多选 */
  multiple?: boolean;
  /** 选择确认回调 */
  onConfirm: (templateIds: number[]) => void;
  /** 取消回调 */
  onCancel: () => void;
  /** 预览模板回调 */
  onPreview?: (templateId: number) => void;
}

export const RoleTemplateSelector: React.FC<RoleTemplateSelectorProps> = ({
  open,
  templates,
  loading = false,
  selectedIds = [],
  multiple = true,
  onConfirm,
  onCancel,
  onPreview,
}) => {
  const [selected, setSelected] = useState<number[]>(selectedIds);
  const [previewTemplate, setPreviewTemplate] = useState<RoleTemplate | null>(null);

  useEffect(() => {
    if (open) {
      setSelected(selectedIds);
    }
  }, [open, selectedIds]);

  const handleToggle = (templateId: number) => {
    if (multiple) {
      setSelected(prev =>
        prev.includes(templateId)
          ? prev.filter(id => id !== templateId)
          : [...prev, templateId]
      );
    } else {
      setSelected([templateId]);
    }
  };

  const handleSelectAll = () => {
    if (selected.length === templates.length) {
      setSelected([]);
    } else {
      setSelected(templates.map(t => t.id));
    }
  };

  const handleConfirm = () => {
    if (selected.length === 0) {
      message.warning('请至少选择一个模板');
      return;
    }
    onConfirm(selected);
  };

  const handlePreview = (template: RoleTemplate) => {
    if (onPreview) {
      onPreview(template.id);
    } else {
      setPreviewTemplate(template);
    }
  };

  const renderTemplateCard = (template: RoleTemplate) => {
    const isSelected = selected.includes(template.id);
    const scopeInfo = SCOPE_LABELS[template.scope] || { text: template.scope, color: 'default' };
    const icon = TEMPLATE_ICONS[template.icon] || <UserOutlined />;

    return (
      <Col xs={24} sm={12} md={8} key={template.id}>
        <Card
          hoverable
          size="small"
          onClick={() => handleToggle(template.id)}
          style={{
            border: isSelected ? '2px solid #1890ff' : '1px solid #f0f0f0',
            borderRadius: 8,
            cursor: 'pointer',
            position: 'relative',
            transition: 'all 0.3s',
          }}
        >
          {isSelected && (
            <CheckCircleOutlined
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                fontSize: 20,
                color: '#1890ff',
              }}
            />
          )}

          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                background: template.color || '#1890ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: 20,
                marginRight: 12,
              }}
            >
              {icon}
            </div>
            <div style={{ flex: 1 }}>
              <Text strong style={{ fontSize: 15 }}>{template.name}</Text>
              <div>
                <Tag color={scopeInfo.color} style={{ marginRight: 4 }}>{scopeInfo.text}</Tag>
                <Text type="secondary" style={{ fontSize: 12 }}>{template.code}</Text>
              </div>
            </div>
          </div>

          <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>
            {template.description || '暂无描述'}
          </Text>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Space size={12}>
              <Tooltip title="权限数量">
                <Badge
                  count={template.permissionCount || 0}
                  showZero
                  style={{ backgroundColor: '#52c41a' }}
                  size="small"
                />
                <Text type="secondary" style={{ fontSize: 12, marginLeft: 4 }}>权限</Text>
              </Tooltip>
              <Tooltip title="菜单数量">
                <Badge
                  count={template.menuCount || 0}
                  showZero
                  style={{ backgroundColor: '#1890ff' }}
                  size="small"
                />
                <Text type="secondary" style={{ fontSize: 12, marginLeft: 4 }}>菜单</Text>
              </Tooltip>
            </Space>
            <Tooltip title="预览详情">
              <Button
                type="text"
                size="small"
                icon={<InfoCircleOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  handlePreview(template);
                }}
              />
            </Tooltip>
          </div>

          <div style={{ marginTop: 8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              数据范围: {DATA_SCOPE_LABELS[template.dataScope] || template.dataScope}
            </Text>
          </div>
        </Card>
      </Col>
    );
  };

  return (
    <>
      <Modal
        title={
          <Space>
            <ThunderboltOutlined />
            <span>选择角色模板</span>
          </Space>
        }
        open={open}
        onCancel={onCancel}
        width={800}
        footer={[
          multiple && (
            <Checkbox
              key="selectAll"
              checked={selected.length === templates.length && templates.length > 0}
              indeterminate={selected.length > 0 && selected.length < templates.length}
              onChange={handleSelectAll}
              style={{ float: 'left', marginTop: 5 }}
            >
              全选
            </Checkbox>
          ),
          <Button key="cancel" onClick={onCancel}>取消</Button>,
          <Button
            key="confirm"
            type="primary"
            onClick={handleConfirm}
            disabled={selected.length === 0}
          >
            应用 {selected.length > 0 ? `(${selected.length})` : ''}
          </Button>,
        ].filter(Boolean)}
      >
        <Spin spinning={loading}>
          {templates.length === 0 ? (
            <Empty description="暂无可用模板" />
          ) : (
            <Row gutter={[16, 16]} style={{ maxHeight: 500, overflow: 'auto', padding: '4px 0' }}>
              {templates.map(renderTemplateCard)}
            </Row>
          )}
        </Spin>
      </Modal>

      {/* 模板预览弹窗 */}
      <Modal
        title={previewTemplate ? `模板预览 - ${previewTemplate.name}` : '模板预览'}
        open={!!previewTemplate}
        onCancel={() => setPreviewTemplate(null)}
        footer={[
          <Button key="close" onClick={() => setPreviewTemplate(null)}>关闭</Button>,
        ]}
        width={600}
      >
        {previewTemplate && (
          <div>
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="模板名称">{previewTemplate.name}</Descriptions.Item>
              <Descriptions.Item label="模板编码">{previewTemplate.code}</Descriptions.Item>
              <Descriptions.Item label="范围">
                <Tag color={SCOPE_LABELS[previewTemplate.scope]?.color}>
                  {SCOPE_LABELS[previewTemplate.scope]?.text}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="角色类型">{previewTemplate.roleType}</Descriptions.Item>
              <Descriptions.Item label="数据范围" span={2}>
                {DATA_SCOPE_LABELS[previewTemplate.dataScope] || previewTemplate.dataScope}
              </Descriptions.Item>
              <Descriptions.Item label="描述" span={2}>
                {previewTemplate.description || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="权限数">{previewTemplate.permissionCount || 0}</Descriptions.Item>
              <Descriptions.Item label="菜单数">{previewTemplate.menuCount || 0}</Descriptions.Item>
              <Descriptions.Item label="版本">v{previewTemplate.version}</Descriptions.Item>
              <Descriptions.Item label="只读">
                {previewTemplate.isReadonly ? '是' : '否'}
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Modal>
    </>
  );
};

export default RoleTemplateSelector;
